import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useRBAC } from '../context/RBACContext';
import { useLanguage } from '../context/LanguageContext';
import { clearUnifiedAuth } from '../utils/unifiedAuth';
import { resolveDashboardRouteForActiveWorkspace } from '../utils/dashboardRouteResolver';
import { roleLabel } from '../utils/canonicalRoleLabels';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { Menu, X, Globe, User, LogOut, Shield, Building2, ChevronDown, Users, BarChart3, MessageSquare, Settings, LayoutDashboard, ListChecks } from 'lucide-react';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { can, isAuthenticated, isSuperAdmin, isClinicAdmin, isDoctor, canSwitchClinic, allowedClinics, activeClinicId, setActiveClinic, clearRBAC, activeWorkspace, availableWorkspaces, activeWorkspaceId, activeRoleId } = useRBAC();
  const { language, setLanguage, enabledLanguages } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [clinicMenuOpen, setClinicMenuOpen] = useState(false);
  // PROVIDER-NAVIGATION-DROPDOWN-01 — consolidates the public-nav
  // "Join as Provider" + "Check Status" links into a single
  // "For Providers" dropdown. ESC + click-outside scoped to this
  // dropdown only (existing Lang/Clinic switchers retain their
  // simpler toggle pattern — out of scope per task).
  const [providerMenuOpen, setProviderMenuOpen] = useState(false);
  const providerMenuRef = useRef(null);
  useEffect(() => {
    if (!providerMenuOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setProviderMenuOpen(false); };
    const handleClick = (e) => {
      if (providerMenuRef.current && !providerMenuRef.current.contains(e.target)) {
        setProviderMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [providerMenuOpen]);

  const languages = enabledLanguages.map(l => ({ code: l.code, name: l.nativeName }));

  // Unified logout: clear both legacy auth and RBAC context
  const handleLogout = () => {
    clearRBAC();
    clearUnifiedAuth();
    if (logout) logout();
    navigate('/login');
  };

  // Get the unified user from localStorage for display name
  const unifiedUser = (() => {
    try { return JSON.parse(localStorage.getItem('treatx_unified_user') || 'null'); } catch { return null; }
  })();

  const displayName = unifiedUser?.name || unifiedUser?.email || user?.name || '';
  // OF-001D #3 — Header identity badge resolves through the single
  // canonical resolver.  No local role label map allowed.
  //
  // UAT-PROD-PROVIDER-MESSAGING-ACCESS-01 — navigation authority is the
  // ACTIVE workspace role from the canonical RBAC context, never the
  // stale login-time `treatx_unified_user.role` snapshot in
  // localStorage. A held-but-inactive role must never reveal a link.
  const activeRole = activeWorkspace?.role_id || activeRoleId || '';
  const displayRole = roleLabel(activeRole) || activeRole || '';

  const hasUnifiedToken = typeof window !== 'undefined' && !!null;

  // P0-RBAC: Derive all access flags from the ACTIVE ROLE, not from broad permission checks
  // This prevents cross-role navigation (e.g., TreatX admin seeing clinic nav)
  // RBAC-FE: canonical RBAC-A1 vocabulary only.
  const isTreatXAdmin = activeRole === 'treatx_super_admin' || activeRole === 'treatx_admin';
  const isActiveClinicAdmin = activeRole === 'provider_admin';
  const isActiveSalesRep = activeRole === 'provider_sales_rep';
  const isActiveDoctor = activeRole === 'provider_doctor';
  const isActivePatient = activeRole === 'patient' || ((isAuthenticated || hasUnifiedToken) && !isTreatXAdmin && !isActiveClinicAdmin && !isActiveSalesRep && !isActiveDoctor && !['provider_coordinator', 'provider_accountant', 'provider_after_sales_rep', 'provider_staff'].includes(activeRole));
  const isActiveCoordinator = activeRole === 'provider_coordinator';
  const isActiveAccountant = activeRole === 'provider_accountant';

  // Role-isolated access flags
  const hasClinicAccess = isActiveClinicAdmin || isActiveSalesRep || isActiveDoctor;
  // WORKFLOW-OPS-001-PHASE-2A: every provider role gets the canonical
  // My Tasks surface in the global header (P0-C requirement).
  const hasProviderRole = (
    isActiveClinicAdmin ||
    isActiveSalesRep ||
    isActiveDoctor ||
    isActiveCoordinator ||
    isActiveAccountant ||
    activeRole === 'provider_after_sales_rep'
  );
  const hasAdminAccess = isTreatXAdmin;
  // P0-FIN-REPORT-SECURITY-01 / ISO-ROLE-CONTRACT-VISIBILITY-01 — the finance
  // nav follows the PERMISSION, not a role list. The hardcoded set drifted
  // once already (`treatx_accountant` was missing) and would drift again with
  // every new role; `can()` reads the active context's own permissions, which
  // is the same `accounting.view` the route guard and the server enforce. The
  // link is only cosmetic: the route guard and the API are the authority.
  const hasAccountingAccess = can('accounting.view');
  // UAT-PROD-PROVIDER-MESSAGING-ACCESS-01 — canonical messaging nav:
  // ACTIVE provider role + the `messaging.view` permission of that
  // active context. Coordinator / After-Sales were previously excluded
  // by the hard-coded `hasClinicAccess` list even though the backend
  // grants them messaging.
  const hasMessagingAccess = hasProviderRole && can('messaging.view');
  const hasUserManagement = isActiveClinicAdmin;
  const isPatient = isActivePatient;

  // OF-001D — "My Dashboard" route resolved STRICTLY from the active
  // workspace's `default_route`, never from RBAC permission flags.
  // Pass `activeWorkspaceId` so the resolver can recover from a
  // transient null `activeWorkspace` (the OWNER VALIDATION FAILURE
  // race — `setWorkspaces` reconciliation briefly blanks the derived
  // object while the persisted id is still correct).
  const getDashboardRoute = () =>
    resolveDashboardRouteForActiveWorkspace(activeWorkspace, availableWorkspaces, activeWorkspaceId);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">T</span>
            </div>
            <span className="text-2xl font-bold text-secondary tracking-tight">TreatX</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-3 lg:gap-6 text-xs lg:text-sm">
            <Link to="/treatments" className="text-foreground hover:text-primary transition-colors" data-testid="nav-treatments">
              {t('nav.treatments')}
            </Link>
            <Link to="/about" className="text-foreground hover:text-primary transition-colors" data-testid="nav-about">
              {t('nav.about')}
            </Link>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors" data-testid="nav-contact">
              {t('nav.contact')}
            </Link>

            {/* Authenticated navigation — permission-based */}
            {(isAuthenticated || isPatient) && (
              <>
                {/* P0-4: "My Dashboard" — ALWAYS visible for every authenticated user */}
                <Link to={getDashboardRoute()} className="text-foreground hover:text-primary transition-colors flex items-center gap-1 font-medium" data-testid="nav-my-dashboard">
                  <LayoutDashboard className="w-4 h-4" /> {t('provider.shell.myDashboard')}
                </Link>

                {/* WORKFLOW-OPS-001-PHASE-2A — canonical My Tasks surface
                    for every provider role (no patient). */}
                {hasProviderRole && (
                  <Link to="/my-tasks" className="text-foreground hover:text-primary transition-colors flex items-center gap-1 font-medium" data-testid="nav-my-tasks">
                    <ListChecks className="w-4 h-4" /> {t('provider.shell.myTasks')}
                  </Link>
                )}

                {/* Team — visible if can("user.view") */}
                {hasUserManagement && (
                  <Link to="/clinic/admin/users" className="text-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="nav-user-management">
                    <Users className="w-4 h-4" /> {t('provider.shell.team')}
                  </Link>
                )}

                {/* Messaging — visible if can("messaging.view") */}
                {hasMessagingAccess && (
                  <Link to="/clinic/messages" className="text-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="nav-messaging">
                    <MessageSquare className="w-4 h-4" /> {t('provider.shell.messages')}
                  </Link>
                )}

                {/* TreatX Admin — visible to platform admins only */}
                {hasAdminAccess && (
                  <Link to="/treatx-admin/dashboard" className="text-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="nav-admin-portal">
                    <Shield className="w-4 h-4" /> {t('provider.shell.admin')}
                  </Link>
                )}

                {/* Accounting — visible to canonical `accounting.view` holders */}
                {hasAccountingAccess && (
                  <Link to="/accounting/dashboard" className="text-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="nav-accounting">
                    <BarChart3 className="w-4 h-4" /> {t('provider.shell.accounting')}
                  </Link>
                )}
              </>
            )}

            {/* Non-authenticated links — PROVIDER-NAVIGATION-DROPDOWN-01 */}
            {!isAuthenticated && !user && !hasUnifiedToken && (
              <div className="relative" ref={providerMenuRef}>
                <button
                  type="button"
                  onClick={() => setProviderMenuOpen((o) => !o)}
                  className="flex items-center gap-1 text-foreground hover:text-primary transition-colors font-medium"
                  aria-haspopup="menu"
                  aria-expanded={providerMenuOpen}
                  data-testid="nav-for-providers"
                >
                  {t('provider.shell.forProviders')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${providerMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {providerMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50"
                    data-testid="nav-for-providers-dropdown"
                  >
                    <Link
                      to="/provider/register"
                      onClick={() => setProviderMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary transition-colors"
                      role="menuitem"
                      data-testid="nav-become-provider"
                    >
                      {t('provider.shell.becomeProvider')}
                    </Link>
                    <Link
                      to="/provider-benefits"
                      onClick={() => setProviderMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary transition-colors"
                      role="menuitem"
                      data-testid="nav-provider-benefits"
                    >
                      {t('provider.shell.providerBenefits')}
                    </Link>
                    <div className="my-1 border-t border-slate-100" />
                    <Link
                      to="/clinic/application-status"
                      onClick={() => setProviderMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary transition-colors"
                      role="menuitem"
                      data-testid="nav-check-status"
                    >
                      Check Application Status
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* OF-001B — Multi-workspace switcher (self-hides for single-workspace users). */}
            <WorkspaceSwitcher />

            {/* Clinic Context Switcher — doctors with multiple clinics only */}
            {canSwitchClinic && (
              <div className="relative">
                <button
                  onClick={() => setClinicMenuOpen(!clinicMenuOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors text-sm font-medium"
                  data-testid="clinic-context-switcher"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  {activeClinicId || 'Select Clinic'}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {clinicMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-testid="clinic-context-dropdown">
                    <div className="px-3 py-2 text-xs text-gray-500 font-medium uppercase tracking-wide border-b">
                      Switch Clinic Context
                    </div>
                    {allowedClinics.map(cid => (
                      <button
                        key={cid}
                        onClick={() => { setActiveClinic(cid); setClinicMenuOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          cid === activeClinicId ? 'bg-teal-50 text-teal-700 font-medium' : 'hover:bg-slate-50 text-gray-700'
                        }`}
                        data-testid={`clinic-option-${cid}`}
                      >
                        <Building2 className="w-4 h-4" />
                        {cid}
                        {cid === activeClinicId && <span className="ml-auto text-teal-500 text-xs">{t('nav.active')}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
                data-testid="language-selector"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-sm font-medium">{language}</span>
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 transition-colors"
                      data-testid={`lang-${lang.code}`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Menu */}
            {isAuthenticated || user ? (
              <div className="flex items-center gap-3">
                {displayName && (
                  <span className="text-sm text-gray-600 hidden lg:block" data-testid="user-display-name">
                    {displayName}
                    {displayRole && <span className="text-xs text-gray-400 ml-1">({displayRole})</span>}
                  </span>
                )}
                <Link
                  to="/settings/change-password"
                  className="flex items-center gap-2 text-foreground hover:text-primary transition-colors text-sm"
                  data-testid="nav-change-password"
                  title={t('nav.changePassword')}
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden lg:inline">{t('nav.security')}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-foreground hover:text-destructive transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-primary" data-testid="login-button">
                {t('nav.login')}
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-foreground"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/20" data-testid="mobile-menu">
            <nav className="flex flex-col gap-3">
              <Link to="/treatments" className="text-foreground hover:text-primary transition-colors">{t('nav.treatments')}</Link>
              <Link to="/about" className="text-foreground hover:text-primary transition-colors">{t('nav.about')}</Link>
              <Link to="/contact" className="text-foreground hover:text-primary transition-colors">{t('nav.contact')}</Link>

              {(isAuthenticated || isPatient) && (
                <>
                  <Link to={getDashboardRoute()} className="text-foreground hover:text-primary flex items-center gap-2" data-testid="mobile-nav-dashboard"><LayoutDashboard className="w-4 h-4" /> {t('provider.shell.myDashboard')}</Link>
                  {hasUserManagement && <Link to="/clinic/admin/users" className="text-foreground hover:text-primary flex items-center gap-2"><Users className="w-4 h-4" /> {t('provider.shell.team')}</Link>}
                  {hasMessagingAccess && <Link to="/clinic/messages" className="text-foreground hover:text-primary flex items-center gap-2" data-testid="mobile-nav-messaging"><MessageSquare className="w-4 h-4" /> {t('provider.shell.messages')}</Link>}
                  {hasAdminAccess && <Link to="/treatx-admin/dashboard" className="text-foreground hover:text-primary flex items-center gap-2"><Shield className="w-4 h-4" /> {t('provider.shell.admin')}</Link>}
                  {hasAccountingAccess && <Link to="/accounting/dashboard" className="text-foreground hover:text-primary flex items-center gap-2"><BarChart3 className="w-4 h-4" /> {t('provider.shell.accounting')}</Link>}
                </>
              )}

              {!isAuthenticated && !user && !hasUnifiedToken && (
                <div className="pt-2 border-t border-white/20" data-testid="mobile-for-providers-group">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">{t('provider.shell.forProviders')}</p>
                  <Link to="/provider/register" className="block py-1.5 text-primary font-medium" data-testid="mobile-nav-become-provider">{t('provider.shell.becomeProvider')}</Link>
                  <Link to="/provider-benefits" className="block py-1.5 text-foreground hover:text-primary" data-testid="mobile-nav-provider-benefits">{t('provider.shell.providerBenefits')}</Link>
                  <Link to="/clinic/application-status" className="block py-1.5 text-foreground hover:text-primary" data-testid="mobile-nav-check-status">{t('nav.checkApplicationStatus')}</Link>
                </div>
              )}

              {/* Mobile clinic switcher */}
              {canSwitchClinic && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500 mb-2">{t('nav.providerContext')}</p>
                  {allowedClinics.map(cid => (
                    <button key={cid} onClick={() => setActiveClinic(cid)}
                      className={`block w-full text-left px-3 py-2 text-sm rounded ${cid === activeClinicId ? 'bg-teal-50 text-teal-700 font-medium' : 'hover:bg-slate-50'}`}
                    >
                      {cid} {cid === activeClinicId && t('nav.activeParen')}
                    </button>
                  ))}
                </div>
              )}

              {isAuthenticated || user ? (
                <>
                  <Link to="/settings/change-password" className="text-foreground hover:text-primary transition-colors flex items-center gap-2" data-testid="mobile-nav-change-password">
                    <Settings className="w-4 h-4" /> {t('nav.changePassword')}
                  </Link>
                  <button onClick={handleLogout} className="text-left text-foreground hover:text-destructive transition-colors flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" className="text-primary font-medium">{t('nav.login')}</Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
